import React, { useState, useEffect } from 'react';
import { Users, CheckCircle, Clock, AlertCircle, Loader2 } from 'lucide-react';

export default function AdminDashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/dashboard', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Fehler beim Laden der Dashboard-Daten');
      
      const data = await response.json();
      setDashboardData(data);
    } catch (err) {
      console.error(err);
      setError('Konnte Dashboard-Daten nicht laden.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-secondary">
        <Loader2 className="w-8 h-8 animate-spin mb-4 text-accent" />
        <p>Lade Echtzeit-Daten...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-600 p-4 rounded-md border border-red-200">
        {error}
      </div>
    );
  }

  // Sichere Fallbacks falls Daten unvollständig sind
  const stats = [
    { title: 'Neue Bewerbungen', value: dashboardData?.applications || 0, icon: Users, color: 'text-blue-500', bg: 'bg-blue-50' },
    { title: 'Aktive Tester', value: dashboardData?.active_testers || 0, icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50' },
    { title: 'Laufende Tests', value: dashboardData?.running_tests || 0, icon: Clock, color: 'text-orange-500', bg: 'bg-orange-50' },
    { title: 'Pendente Aufgaben', value: dashboardData?.pending_tasks || 0, icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-50' },
  ];

  const recentApps = dashboardData?.recent_applications || [];

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-primary">Willkommen zurück, Admin!</h1>
        <p className="text-muted-foreground">Hier ist Ihre Echtzeit-Übersicht für die digitale Qualitätssicherung heute.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="bg-white p-6 rounded-xl border border-border shadow-sm flex items-center gap-4">
              <div className={`p-4 rounded-lg ${stat.bg}`}>
                <Icon className={`w-6 h-6 ${stat.color}`} />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                <h3 className="text-2xl font-bold text-primary">{stat.value}</h3>
              </div>
            </div>
          );
        })}
      </div>

      {/* Letzte Aktivitäten */}
      <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-border bg-slate-50/50 flex justify-between items-center">
          <h2 className="font-semibold text-primary">Letzte Bewerbungseingänge</h2>
          <span className="text-xs bg-slate-200 text-slate-700 px-2.5 py-1 rounded-full font-medium">Neueste zuerst</span>
        </div>
        <div className="p-0">
          {recentApps.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              Aktuell sind keine neuen Bewerbungen zur Prüfung vorhanden.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {recentApps.map((app, index) => {
                const name = app?.name || 'Unbekannter Bewerber';
                const role = app?.role || 'Tester';
                const date = app?.date || 'Unbekannt';
                const initial = name.charAt(0) ? name.charAt(0).toUpperCase() : 'U';

                return (
                  <div key={index} className="flex items-center justify-between p-6 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center text-secondary font-bold">
                        {initial}
                      </div>
                      <div>
                        <p className="font-medium text-primary">{name}</p>
                        <p className="text-sm text-muted-foreground">Bewerbung als {role}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground hidden sm:inline-block">{date}</span>
                      <span className="px-2.5 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full font-medium">
                        Neu
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}